import React, { useEffect, useState, useRef } from "react";
import { Box, Breakpoint, Button, CardMedia, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Fade, Grid, Paper, Slide, TextField, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Formik, FormikErrors, FormikHelpers, FormikState, FormikTouched } from "formik";
import * as yup from 'yup';
import { MapkitProvider } from "react-mapkit";
import { AppColor, CreatingBeaconViewMode, Fonts } from "../../../../types/Constants";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { deleteBeaconFace, fetchError, getMapkitToken, getUserDetail, makeBeacon, showMessage, updateBeacon, uploadBeaconFace, uploadBeaconImage } from "../../../../redux/actions";
import { Beacon, UserProfile } from '../../../../types/models/Beacon';
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import { convertToUpdateBeacon } from "../../../../utils/utils";
import { Coordinate } from "../../../../types/models/Mapkit";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import AppScrollbar from "../../../../components/AppScrollbar";
import BeaconForm from "./BeaconForm";
import BeaconPreview from "./BeaconPreview";
import BeaconScheduleForm from "./BeaconScheduleForm";
import BeaconPlacementForm from "./BeaconPlacementForm";
import BeaconStyleForm from "./BeaconStyleForm";
import ConfirmModerationDialog, { ModerationActions } from "../../../../components/ConfirmModerationDialog";
import { EmailOutlined, ArrowDownward } from "@mui/icons-material";
import { useAuthContext } from "../../../authentication/contexts/AuthContext";
import BeaconDetailsPreview from "./BeaconDetailsPreview";
import { Category } from "../../../../types/models/Category";
import BeaconModelPreview from "./BeaconModelPreview";

export const BeaconEditorModes = ["CREATE", "EDIT", "MOD_ONLY", "READ_ONLY"] as const;

interface BeaconEditorProps {
    maxWidth?: Breakpoint;
    isOpen: boolean;
    onSubmitted: () => void;
    onClose: () => void;
    onOpenBeaconDeleteDialog:(beacon?: Beacon) => void;
    beacon?: Beacon;
    mode?: typeof BeaconEditorModes[number];
    mapToken: {
        tokenOrCallback: string;
      };
}

const BeaconEditor: React.FC<BeaconEditorProps> = (prop) => {
    // Util
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const fullscreen = useMediaQuery(theme.breakpoints.down('md'));
    const auth = useAuthContext();

    const [currentImageModalFile, setCurrentImageModalFile] = useState<File | undefined>(undefined);
    const [currentImageFaceFile, setCurrentImageFaceFile] = useState<File | undefined>(undefined);
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }>({
        width: 0,
        height: 0,
    });
    const [deleteFaceImage, setDeleteFaceImage] = useState(false);
    const [currentCoordinate, setCurrentCoordinate] = useState<Coordinate>();
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [moderationConfirmDialogOpen, setModerationConfirmDialogOpen] = useState<boolean>(false);
    const [loadingUserProfile, setLoadingUserProfile] = useState<boolean>(false);
    const [userProfile, setUserProfile] = useState<UserProfile>();
    const [moderationAction, setModerationAction] = useState<typeof ModerationActions[number]>("REMOVE");

    const [scrollY, setScrollY] = useState(0);

    const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const scrollToSection = (index: number) => {
        if (containerRef.current && sectionRefs.current[index]) {

            const section = sectionRefs.current[index];
            const offsetTop = section ? section.offsetTop : 0;

            containerRef.current.scrollTo({
            top: offsetTop - 175,
            behavior: "smooth",
            });
        }
      };

    const businessGroupState = useAppSelector(state => state.businessGroup);

    const initialValues: MakeBeacon = {
      name: prop.beacon?.display_name ?? '',
      beacon_group_id: businessGroupState.currentBusinessGroup?.id ?? "",
      description: prop.beacon?.description ?? "",
      category: prop.beacon?.category ?? undefined,
      link: prop.beacon?.link ?? "",
      summary: prop.beacon?.summary ?? undefined,
      image_id: prop.beacon?.image_id ?? undefined,
      startDate: prop.beacon?.start_time ?? "",
      endDate: prop.beacon?.end_time ?? "",
      latitude: prop.beacon?.latitude ?? undefined,
      longitude: prop.beacon?.longitude ?? undefined,
      model: prop.beacon?.model ?? "default",
      show_distance: prop.beacon?.show_distance ?? true
    };
    
    const handleScroll = () => { setScrollY(containerRef.current?.scrollTop ?? 0); }; 

    useEffect(() => {
      containerRef.current?.addEventListener('scroll', handleScroll);
      return () => { containerRef.current?.removeEventListener('scroll', handleScroll); };

    }, []);

    useEffect(() => {
        // Get user data if moderating. Used to display rich user attributes.
        if (
          prop.beacon?.owner_id &&
          prop.isOpen === true &&
          auth.userRoles?.includes("admin") &&
          prop.mode === "MOD_ONLY" // Functionally this means "moderation only" for now.
        ) {
          setLoadingUserProfile(true);
          dispatch(
            getUserDetail(prop.beacon?.owner_id, (success, data) => {
              if (success) {
                setUserProfile(data);
                setLoadingUserProfile(false);
              } else {
                setLoadingUserProfile(false);
              }
            })
          );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prop.isOpen]);

    useEffect(() => {
    // Trigger to update preview address when edit a beacon
    if (prop.beacon) {
        const coordinate: Coordinate = {
        latitude: prop.beacon.latitude,
        longitude: prop.beacon.longitude
        };
        setCurrentCoordinate(coordinate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prop.beacon]);

    const validationSchema = yup.object({
        name: yup.string().required("Please enter Beacon name").max(40, "Beacon name must be at most 40 characters"),
        category: yup.string().required("Please select a category"),
        link: yup.string().matches(/^(https?:\/\/)?[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+(?:\/[a-zA-Z0-9-._~:?#&%=+,;]*)*$/, 'https://example.com, http://example.com, example.com'),
        startDate: yup.date().default(() => new Date()),
        endDate: yup.date().when("startDate", (startDate, schema) => startDate && schema.min(startDate, "The beacon must end after it starts")),
        latitude: yup.number().required("Please select a location"),
        longitude: yup.number().required("Please select a location"),
        model: yup.string().required("Please select a model type")
      });

    const handleCategorySelect = (category: Category) => {
      setSelectedCategory(category);
    };

    const handleSubmit = (values: MakeBeacon, formikHelpers: FormikHelpers<MakeBeacon>, ) => {
        const beacon_id = prop.beacon?.id;
        const beacon_group_id = businessGroupState.currentBusinessGroup!.id;

        let modifiedValues = { ...values };

        // Check the link if it has https
        if (modifiedValues.link && !/^https?:\/\//i.test(modifiedValues.link)) {
          modifiedValues.link = "https://" + modifiedValues.link;
        }
        
        // Change http:// to https://
        if (modifiedValues.link) {
          modifiedValues.link = modifiedValues.link.replace(/http:\/\//gi, "https://");
        }

        values.link = modifiedValues.link;
        formikHelpers.setFieldValue("link", modifiedValues.link);

        if (!currentImageModalFile) {
            if (prop.mode === "EDIT") {
              // Update beacon without changing the imageModal OR update beacon and delete the imageModal.
              callUpdateBeaconRequest(values, formikHelpers.setSubmitting);
            } else {
              callCreateBeaconRequest(values, formikHelpers.setSubmitting, formikHelpers.resetForm);
            }
        }
        else {
            // Upload imageModal + save the beacon
            dispatch(uploadBeaconImage(beacon_group_id, currentImageModalFile, currentImageModalFile?.size, imageDimensions!.width, imageDimensions!.height,(imageId) => {
            if (imageId) {
                values.image_id = imageId;
                if (prop.mode === "EDIT") {
                callUpdateBeaconRequest(values, formikHelpers.setSubmitting);
                }
                else {
                callCreateBeaconRequest(values, formikHelpers.setSubmitting, formikHelpers.resetForm);
                }
            }
            else {
                // Failed to save imageModal
                formikHelpers.setSubmitting(false);
            }
            }));
        }
        if (deleteFaceImage) {
            // Delete the beacon image face
            dispatch(deleteBeaconFace(beacon_id ?? "", beacon_group_id, (result) => {
            if (result) {
                // Successfully delete the image
                console.log("Deleted face image");
            } else {
                // Image didn't exist on beacon
                console.log("Face image failed to delete or didn't exist");
            }
            }));
        }
    };

    // Attempts to open users default mail client to the beacon owner.
    function emailBeaconOwner() {
        const email = userProfile?.UserAttributes.find(
        (x) => x.Name === "email"
        )?.Value;

        if (email) {
        window.open(`mailto:${email}`, "_blank");
        }
    }

    const initialiseStates = () => {
        setCurrentImageModalFile(undefined);
        setCurrentImageFaceFile(undefined);
        setDeleteFaceImage(false);
        setCurrentCoordinate(undefined);
      };
    
      const callCreateBeaconRequest = (
        beacon: MakeBeacon,
        setSubmitting: (isSubmitting: boolean) => void,
        resetForm: (nextState?: Partial<FormikState<MakeBeacon>> | undefined) => void) => {
        
        beacon.startDate = beacon.startDate == "" ? null : beacon.startDate;
        beacon.endDate = beacon.endDate == "" ? null : beacon.endDate;

        // Call post API to save the beacon
        dispatch(makeBeacon(beacon, async (result) => {
          if (result.success && result.beaconId) {
            const beaconId = result.beaconId;
            const groupId = businessGroupState.currentBusinessGroup?.id;
            if (currentImageFaceFile && groupId) {
              const uploadResult = await new Promise((resolve, reject) => {
                uploadBeaconFaceImage(
                  beaconId,
                  currentImageFaceFile,
                  groupId,
                  (result) => {
                    if (!result) {
                      dispatch(fetchError("Failed to upload face image"));
                      reject(new Error("Upload failed"));
                    } else {
                      resolve(result);
                    }
                  }
                );
              });
              
              if (!uploadResult) {
                setSubmitting(false);
                return;
              }
            }
            resetForm();
            initialiseStates();
            prop.onSubmitted();
            dispatch(showMessage("The beacon has been created"));
    
          }
          setSubmitting(false);
        }
        ));
      };
    
      const callUpdateBeaconRequest = (beacon: MakeBeacon, setSubmitting: (isSubmitting: boolean) => void) => {
        const beaconId = prop.beacon!.id;
        const beaconGroupId = businessGroupState.currentBusinessGroup?.id;
        // Call post API to save the beacon
    
        if (currentImageFaceFile) {
          uploadBeaconFaceImage(beaconId, currentImageFaceFile, businessGroupState.currentBusinessGroup!.id, (result) => {
            if (!result) {
              dispatch(fetchError("Failed to upload face image"));
            }
          });
        }
    
        const updateRequest = convertToUpdateBeacon(beacon, beaconId ?? "", beaconGroupId ?? "");
        dispatch(updateBeacon(updateRequest, (result) => {
          if (result) {
            dispatch(showMessage("The beacon has been saved"));
    
            initialiseStates();
            prop.onSubmitted();
    
          }
          setSubmitting(false);
        }
        ));
      };
    
      const uploadBeaconFaceImage = (
        beaconId: string,
        faceFile: File,
        beaconGroupId: string,
        onComplete: (success: boolean) => void
      ) => {
        const formData = new FormData();
        formData.append('file', faceFile, faceFile.name);
      
        dispatch(uploadBeaconFace(beaconId, formData, beaconGroupId, (result) => {
          if (result) {
            setCurrentImageFaceFile(undefined);
            onComplete(true);
          } else {
            onComplete(false);
          }
        }));
      };

    const InputForm = (
        values: MakeBeacon,
        errors: FormikErrors<MakeBeacon>,
        handleChange: (e: React.ChangeEvent<any>) => void,
        touched: FormikTouched<MakeBeacon>,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<MakeBeacon>>,
        setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void
      ) => {

        return (
            <div
            id="scrollable-container"
            style={{ overflowY: "auto", height: "calc(100vh - 180px)" }}
            ref={containerRef}
            >
            <MapkitProvider {...prop.mapToken}>
              <Box>
                <Paper id="section-1" ref={(el) => sectionRefs.current[0] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
                <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 1: Name</Typography>
                <Box sx={{ display: "flex", justifyContent:"space-between", alignItems: "end", p: 2 }}>
                <TextField
                  disabled={prop.mode === "MOD_ONLY" || prop.mode === "READ_ONLY"}
                  required
                  name="display_title"
                  value={values.name}
                  variant="outlined"
                  placeholder="..."
                  id="display_title"
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setFieldValue("name", newValue);
                  }}
                  error={((errors.name ? true : undefined) && touched.name)}
                  autoComplete="off"
                  aria-autocomplete="none"
                  helperText={errors.name && touched.name ? errors.name : `${values.name.length}/${40}`}
                  inputProps={{ maxLength: 40 }}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: 'right',
                    },
                  }}
                  sx={{ width: "300px" }}
                />
                  <Button onClick={() => scrollToSection(1)}>Next <ArrowDownward sx={{ ml: 4 }} /></Button>
                </Box>
                </Paper>
              </Box>
              <Box>
                <Paper id="section-2" ref={(el) => sectionRefs.current[1] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
                <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 2: Schedule</Typography>
                <Box sx={{ display: "flex", justifyContent:"space-between", alignItems: "end" }}>
                <BeaconScheduleForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  mode={prop.mode}
                />
                <Button onClick={() => scrollToSection(2)}>Next <ArrowDownward sx={{ ml: 4 }} /></Button>
                </Box>
                </Paper>
              </Box>
              <Box>
                <Paper id="section-3" ref={(el) => sectionRefs.current[2] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4 }}>
                  <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 3: Location</Typography>
                  <Box sx={{ display: "flex", justifyContent:"space-between", alignItems: "end" }}>
                    <Box sx={{ width: "55%", height: "90%" }}>
                      <BeaconPlacementForm
                          mode={prop.mode}
                          values={values}
                          errors={errors}
                          touched={touched}
                          handleChange={handleChange}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          setCurrentCoordinate={setCurrentCoordinate} />
                    </Box>
                    <Button onClick={() => scrollToSection(3)}>Next <ArrowDownward sx={{ ml: 4 }} /></Button>
                  </Box>
                </Paper>
              </Box>
              <Box>
              <Paper id="section-4" ref={(el) => sectionRefs.current[3] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4 }}>
              <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 4: Appearance</Typography>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Box sx={{ flex: 1, mt: 4 }}>
                <BeaconForm
                  mode={prop.mode}
                  values={values}
                  errors={errors}
                  handleChange={handleChange}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  onCategorySelect={(category) => handleCategorySelect(category)}
                  groups={businessGroupState.businessGroups ?? []}
                  imageId2={prop.mode === "EDIT" && prop.beacon?.has_custom_beacon_face && !deleteFaceImage ? prop.beacon?.id : undefined}
                  handleFileChange2={(file) => {
                    if (prop.mode === "EDIT" && !file) {
                      // Remove file
                      // setFieldValue("image_id", undefined);
                    }
                    setCurrentImageFaceFile(file);
                  }}
                  handleDeleteFace={(deleteFaceImage) => {
                    setDeleteFaceImage(deleteFaceImage);
                  }}
                />
                </Box>
                <Box sx={{ flex: 1, justifyContent: "center", alignItems: "center", display: "flex" }}>
                  {/* This is the component where the 3D Model Preview will go */}
                  <BeaconModelPreview
                    category={selectedCategory}
                    beaconImage={currentImageFaceFile}
                    beaconImageId={prop.mode === "EDIT" && prop.beacon?.has_custom_beacon_face && !deleteFaceImage ? prop.beacon?.id : undefined}
                    beaconName={values.name}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "end" }}>
              <Button onClick={() => scrollToSection(4)}>Next <ArrowDownward sx={{ ml: 4 }} /></Button>
              </Box>
              </Paper>
              </Box>
              <Box>
              <Paper id="section-5" ref={(el) => sectionRefs.current[4] = el} sx={{ backgroundColor: "white", padding: 3, mb: 4  }}>
              <Typography sx={{ color: AppColor.darkBrand, fontSize: 20, fontWeight: Fonts.SEMI_BOLD }}>Step 5: Details Screen</Typography>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Box sx={{ flex: 1, mt: 4 }}>
                  <BeaconStyleForm
                    mode={prop.mode}
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    imageId1={prop.mode === "EDIT" ? values.image_id : undefined}
                    handleFileChange={(file, width, height) => {
                      if (prop.mode === "EDIT" && !file) {
                        // Remove file
                        setFieldValue("image_id", undefined);
                      }
                      setCurrentImageModalFile(file);
                      setImageDimensions({ width: width, height: height });
                    }}
                    handleFileChange2={(file) => {
                      if (prop.mode === "EDIT" && !file) {
                        // Remove file
                        // setFieldValue("image_id", undefined);
                      }
                      setCurrentImageFaceFile(file);
                    }}
                    handleDeleteFace={(deleteFaceImage) => {
                      setDeleteFaceImage(deleteFaceImage);
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, justifyContent: "center", alignItems: "flex-start", display: "flex" }}>
                  {/* <BeaconDetailsPreview
                    name={values.name}
                    beaconId={prop.beacon?.id}
                    category={values.category}
                    description={values.description}
                    imageModal={currentImageModalFile}
                    imageId={values.image_id}
                    coordinate={currentCoordinate}
                    startDate={values.startDate}
                    endDate={values.endDate}
                    hasCustomBeaconFace={prop.beacon?.has_custom_beacon_face}
                    lastModified={prop.beacon?.last_modified} 
                  /> */}
                  <BeaconPreview
                    name={values.name}
                    beaconId={prop.beacon?.id}
                    category={values.category}
                    description={values.description}
                    imageModal={currentImageModalFile}
                    imageFace={currentImageFaceFile}
                    imageId={values.image_id}
                    coordinate={currentCoordinate}
                    startDate={values.startDate}
                    hasCustomBeaconFace={prop.beacon?.has_custom_beacon_face}
                    lastModified={prop.beacon?.last_modified}
                  />
                </Box>
              </Box>
              </Paper>
              </Box>
            </MapkitProvider>
            </div>
        );
    };

    return (
        <Formik
        enableReinitialize
        validateOnChange={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, errors, touched, isSubmitting, handleChange, submitForm, resetForm, setFieldValue, setFieldTouched, setSubmitting }) => {
        return (
        <>
        <Box
            sx={{
            "& .MuiDialog-paper": {
                width: fullscreen ? "100%" : "70%",
                maxWidth: "1536px",
                backgroundColor: "white",
            },
            "& .MuiDialogContent-root": {
                overflowY: "auto",
                paddingLeft: 0,
                paddingRight: 0,
            },
            // minWidth: "800px",
            }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", minWidth: "1000px"}}>
            <Box sx={{ display: "flex", gap: 3}}>
              {/* <Typography>container ref: {scrollY}</Typography>
              <Typography>section ref: {sectionRefs.current[2]?.offsetTop}</Typography> */}
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY < 173 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(0)}
              >
                Step 1: Name
              </Button>
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY >= 173 && scrollY <= 325 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(1)}
              >
                Step 2: Schedule
              </Button>
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY > 325 && scrollY <= 885 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(2)}
              >
                Step 3: Location
              </Button>
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY > 885 && scrollY <= 1464 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(3)}
              >
                Step 4: Appearance
              </Button>
              <Button
                sx={{
                  color: AppColor.darkBrand, 
                  fontSize: 16, 
                  fontWeight: Fonts.SEMI_BOLD,
                  textTransform: "none",
                  textDecoration: scrollY > 1464 ? "underline" : "none",
                  "&:hover": { textDecoration: "underline" },
                  flexShrink: 0
                }}
                onClick={() => scrollToSection(4)}
              >
                Step 5: Details Screen
              </Button>
            </Box>
            <Box>
                {/* Chip showing email of beacon owner. */}
                {prop.mode === "MOD_ONLY" && auth.userRoles?.includes('admin') && (
                    <Fade in={!loadingUserProfile}>
                    <Chip
                        icon={<EmailOutlined fontSize="small" />}
                        sx={{ mr: 2 }}
                        clickable
                        onClick={emailBeaconOwner}
                        label={
                        userProfile?.UserAttributes.find(
                            (x) => x.Name === "email"
                        )?.Value
                        }
                    />
                    </Fade>
                )}


                {/* Remove - Moderation - Requires MOD_ONLY */}
                {prop.mode === "MOD_ONLY" && (
                    <Button
                    sx={{ marginRight: 2 }}
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={() => {
                        setModerationAction("REMOVE");
                        setModerationConfirmDialogOpen(true);
                    }}
                    >
                    Remove Beacon
                    </Button>
                )}
                {prop.mode !== "CREATE" && (
                <Button
                  variant="contained"
                  disabled={isSubmitting}
                  onClick={() => { 
                    prop.onOpenBeaconDeleteDialog(prop.beacon); 
                  }}
                  sx={{ marginRight: 4, backgroundColor: AppColor.redError, flexShrink: 0,
                        ":hover":{
                          backgroundColor: AppColor.redError
                        }
                  }}
                >
                  Delete
                </Button>
                )}
                {/* Close / Cancel - Mode-agnostic. */}
                <Button
                    variant="outlined"
                    sx={{ marginRight: 4 }}
                    onClick={() => {
                    resetForm();
                    initialiseStates();
                    prop.onClose();
                    }}
                >
                    {prop.mode === "MOD_ONLY" ? "Close" : "Cancel"}
                </Button>
                {/* Save - General - Only visible outside of mod_only. */}
                {(prop.mode !== "MOD_ONLY" && prop.mode !== "READ_ONLY") && (
                    <Button
                    sx={{ marginRight: 2, flexShrink: 0 }}
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={submitForm}
                    >
                    Save
                    </Button>
                )}
                {/* Ban - Moderation - Requires MOD_ONLY*/}
                {prop.mode === "MOD_ONLY" && (
                    <Button
                    sx={{ marginRight: 2 }}
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={() => {
                        setModerationAction("BAN");
                        setModerationConfirmDialogOpen(true);
                    }}
                    >
                    Ban User
                    </Button>
                )}
            </Box>
          </Box>
          <Divider />
            <Box style={{ paddingTop: 4  }}>
            {/* <AppScrollbar forceVisible="y" autoHide={false}> */}
                    {InputForm(
                    values,
                    errors,
                    handleChange,
                    touched,
                    setFieldValue,
                    setFieldTouched,
                    )}
            {/* </AppScrollbar> */}
            </Box>
        </Box>
        </>
        );
        }
        }
        </Formik >
    );

};
    
export default BeaconEditor;